import * as React from 'react';
import { createRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { ChannelAttributionRow, OwnerScorecard, SalesDashboardWindow } from '@bits/shared/zod';
import { formatInr, paise } from '@bits/domain';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SortableTh } from '@/components/ui/sortable-th';
import { useSortedRows, type SortAccessors } from '@/lib/sort';
import { stageBadgeVariant, stageLabel } from './sales.prospects';

export const salesDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sales/dashboard',
  component: SalesDashboardPage,
});

const WINDOWS: readonly SalesDashboardWindow[] = ['7d', '30d', '90d'];

function formatPct(v: number): string {
  return `${(v * 100).toFixed(1)}%`;
}

type OwnerSortKey =
  | 'owner'
  | 'open'
  | 'activities'
  | 'won'
  | 'lost'
  | 'conv'
  | 'stale'
  | 'pipeline'
  | 'wonValue';
const EMPTY_OWNERS: OwnerScorecard[] = [];
const ownerSortAccessors: SortAccessors<OwnerScorecard, OwnerSortKey> = {
  owner: (o) => o.personName,
  open: (o) => o.openProspects,
  activities: (o) => o.activitiesInWindow,
  won: (o) => o.wonInWindow,
  lost: (o) => o.lostInWindow,
  conv: (o) => o.conversionPct,
  stale: (o) => o.staleCount,
  pipeline: (o) => o.pipelineValuePaise,
  wonValue: (o) => o.wonValuePaise,
};

type ChannelSortKey = 'channel' | 'prospects' | 'won' | 'lost' | 'conv' | 'wonValue';
const EMPTY_CHANNELS: ChannelAttributionRow[] = [];
const channelSortAccessors: SortAccessors<ChannelAttributionRow, ChannelSortKey> = {
  channel: (c) => c.channelName,
  prospects: (c) => c.prospects,
  won: (c) => c.won,
  lost: (c) => c.lost,
  conv: (c) => c.conversionPct,
  wonValue: (c) => c.wonValuePaise,
};

function SalesDashboardPage(): JSX.Element {
  const [window, setWindow] = React.useState<SalesDashboardWindow>('30d');

  const dashQuery = useQuery({
    queryKey: ['sales-dashboard', window],
    queryFn: () => api.getSalesDashboard(window),
  });

  const d = dashQuery.data;
  const { rows: owners, ...ownerSorter } = useSortedRows(d?.owners ?? EMPTY_OWNERS, ownerSortAccessors);
  const { rows: channelRows, ...channelSorter } = useSortedRows(
    d?.channelAttribution ?? EMPTY_CHANNELS,
    channelSortAccessors,
  );
  const maxStagePaise = React.useMemo(() => {
    if (!d) return 0n;
    let m = 0n;
    for (const s of d.stages) {
      const v = BigInt(s.valuePaise);
      if (v > m) m = v;
    }
    return m;
  }, [d]);

  return (
    <AppShell>
      <div className="flex flex-wrap items-start justify-between gap-4 lg:flex-nowrap">
        <div className="flex min-w-0 flex-col gap-2">
          <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
            Sales dashboard
          </h1>
          <p className="text-base text-[hsl(var(--muted-foreground))]">
            Owner scorecards, stage buckets, channel attribution, staleness.
          </p>
        </div>
        <div className="flex items-center gap-1 rounded-xl border border-[hsl(var(--border))] p-1">
          {WINDOWS.map((w) => (
            <Button
              key={w}
              size="sm"
              variant={window === w ? 'default' : 'ghost'}
              onClick={() => setWindow(w)}
            >
              {w}
            </Button>
          ))}
        </div>
      </div>

      {d ? (
        <>
          <div className="grid gap-3 md:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Stale prospects</CardTitle>
                <CardDescription>Untouched past staleness threshold.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="font-display text-4xl font-semibold">{d.staleTotal}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Generated</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">
                  {new Date(d.generatedAt).toLocaleString('en-IN')}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Window</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="font-display text-2xl font-semibold">{d.window}</div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Per-owner scorecard</CardTitle>
              <CardDescription>C-suite rows are highlighted.</CardDescription>
            </CardHeader>
            <CardContent>
              {d.owners.length === 0 ? (
                <p className="text-sm text-[hsl(var(--muted-foreground))]">No owners yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-left text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                      <tr className="border-b border-[hsl(var(--border))]">
                        <SortableTh sortKey="owner" controller={ownerSorter} className="py-2 pr-3">
                          Owner
                        </SortableTh>
                        <SortableTh sortKey="open" controller={ownerSorter} align="right" className="py-2 pr-3">
                          Open
                        </SortableTh>
                        <SortableTh sortKey="activities" controller={ownerSorter} align="right" className="py-2 pr-3">
                          Activities
                        </SortableTh>
                        <SortableTh sortKey="won" controller={ownerSorter} align="right" className="py-2 pr-3">
                          Won
                        </SortableTh>
                        <SortableTh sortKey="lost" controller={ownerSorter} align="right" className="py-2 pr-3">
                          Lost
                        </SortableTh>
                        <SortableTh sortKey="conv" controller={ownerSorter} align="right" className="py-2 pr-3">
                          Conv.
                        </SortableTh>
                        <SortableTh sortKey="stale" controller={ownerSorter} align="right" className="py-2 pr-3">
                          Stale
                        </SortableTh>
                        <SortableTh sortKey="pipeline" controller={ownerSorter} align="right" className="py-2 pr-3">
                          Pipeline
                        </SortableTh>
                        <SortableTh sortKey="wonValue" controller={ownerSorter} align="right" className="py-2 pr-3">
                          Won value
                        </SortableTh>
                      </tr>
                    </thead>
                    <tbody>
                      {owners.map((o) => (
                        <tr
                          key={o.personId}
                          className="border-b border-[hsl(var(--border))]/60"
                        >
                          <td className="py-2 pr-3">
                            <div className="flex items-center gap-2 font-medium">
                              {o.personName}
                              {o.isCsuite ? (
                                <Badge variant="default">
                                  {o.csuiteRole ? o.csuiteRole.toUpperCase() : 'C-suite'}
                                </Badge>
                              ) : null}
                            </div>
                          </td>
                          <td className="py-2 pr-3 text-right tabular-nums">{o.openProspects}</td>
                          <td className="py-2 pr-3 text-right tabular-nums">{o.activitiesInWindow}</td>
                          <td className="py-2 pr-3 text-right tabular-nums">{o.wonInWindow}</td>
                          <td className="py-2 pr-3 text-right tabular-nums">{o.lostInWindow}</td>
                          <td className="py-2 pr-3 text-right tabular-nums">{formatPct(o.conversionPct)}</td>
                          <td className="py-2 pr-3 text-right tabular-nums">
                            {o.staleCount > 0 ? (
                              <span className="text-[hsl(var(--destructive))]">{o.staleCount}</span>
                            ) : (
                              o.staleCount
                            )}
                          </td>
                          <td className="py-2 pr-3 text-right tabular-nums">
                            {formatInr(paise(BigInt(o.pipelineValuePaise)))}
                          </td>
                          <td className="py-2 pr-3 text-right tabular-nums">
                            {formatInr(paise(BigInt(o.wonValuePaise)))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Stage buckets</CardTitle>
              <CardDescription>Count and total value per stage.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-2">
                {d.stages.map((s) => {
                  const v = BigInt(s.valuePaise);
                  const pct =
                    maxStagePaise > 0n ? Number((v * 100n) / maxStagePaise) : 0;
                  return (
                    <div key={s.stage} className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                      <div className="min-w-0 flex-1 sm:w-32 sm:flex-none">
                        <Badge variant={stageBadgeVariant(s.stage)}>{stageLabel(s.stage)}</Badge>
                      </div>
                      <div className="order-last basis-full sm:order-none sm:basis-auto sm:flex-1">
                        <div className="h-2 w-full overflow-hidden rounded-full bg-[hsl(var(--muted))]">
                          <div
                            className="h-full bg-[hsl(var(--brand))]"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                      <div className="text-right text-xs tabular-nums sm:w-20">{s.count}</div>
                      <div className="whitespace-nowrap text-right text-xs tabular-nums sm:w-32">
                        {formatInr(paise(v))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Channel attribution</CardTitle>
              <CardDescription>Which channels convert.</CardDescription>
            </CardHeader>
            <CardContent>
              {d.channelAttribution.length === 0 ? (
                <p className="text-sm text-[hsl(var(--muted-foreground))]">No channel data.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-left text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                      <tr className="border-b border-[hsl(var(--border))]">
                        <SortableTh sortKey="channel" controller={channelSorter} className="py-2 pr-3">
                          Channel
                        </SortableTh>
                        <SortableTh sortKey="prospects" controller={channelSorter} align="right" className="py-2 pr-3">
                          Prospects
                        </SortableTh>
                        <SortableTh sortKey="won" controller={channelSorter} align="right" className="py-2 pr-3">
                          Won
                        </SortableTh>
                        <SortableTh sortKey="lost" controller={channelSorter} align="right" className="py-2 pr-3">
                          Lost
                        </SortableTh>
                        <SortableTh sortKey="conv" controller={channelSorter} align="right" className="py-2 pr-3">
                          Conv.
                        </SortableTh>
                        <SortableTh sortKey="wonValue" controller={channelSorter} align="right" className="py-2 pr-3">
                          Won value
                        </SortableTh>
                      </tr>
                    </thead>
                    <tbody>
                      {channelRows.map((c) => (
                        <tr
                          key={c.channelId}
                          className="border-b border-[hsl(var(--border))]/60"
                        >
                          <td className="py-2 pr-3 font-medium">{c.channelName}</td>
                          <td className="py-2 pr-3 text-right tabular-nums">{c.prospects}</td>
                          <td className="py-2 pr-3 text-right tabular-nums">{c.won}</td>
                          <td className="py-2 pr-3 text-right tabular-nums">{c.lost}</td>
                          <td className="py-2 pr-3 text-right tabular-nums">
                            {formatPct(c.conversionPct)}
                          </td>
                          <td className="py-2 pr-3 text-right tabular-nums">
                            {formatInr(paise(BigInt(c.wonValuePaise)))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      ) : (
        <p className="text-sm text-[hsl(var(--muted-foreground))]">Loading…</p>
      )}
    </AppShell>
  );
}
